import { skipToken } from "@reduxjs/toolkit/query";
import {
  getRecordingS3IntakeSettings,
  type Integration,
} from "@repo/app-types/entities/integration";
import { countRecordingImportCustomerRows } from "@repo/app-types/entities/recordingImport";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useRecordingImportStatusCountsQuery } from "../../../api/entities/recordingImport.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectRecordingS3IntakeIntegration } from "../../integrations/integrationsSlice.js";
import { IntegrationLogo } from "./integrationLogo.js";

const TITLE = "Recording S3 intake";

/**
 * What the Settings and Failed imports tabs share: the company's S3-intake
 * integration row (Five9 or RingCentral) from the boot payload. Handed down
 * through the router `Outlet` so the tabs read it without re-selecting.
 */
export interface RecordingS3IntakeDetailOutletContext {
  integration: Integration;
}

export function useRecordingS3IntakeDetailOutletContext(): RecordingS3IntakeDetailOutletContext {
  return useOutletContext<RecordingS3IntakeDetailOutletContext>();
}

/** Whether the row names a bucket, which is what makes it a recording-import integration. */
export function checkRecordingS3IntakeBucket(
  integration: Integration | null,
): boolean {
  return Boolean(
    integration && getRecordingS3IntakeSettings(integration)?.bucket,
  );
}

/**
 * The "Recording S3 intake" detail page: a wider, tabbed frame (like Genesys)
 * hosting a **Settings** tab (bucket, example key, and — for RingCX customers
 * — the agent email mapping) and a **Failed imports** tab (the integration's
 * action-required and failed `recording_import` rows, with reprocess and skip).
 *
 * The row comes from the boot payload and only exists for intake customers
 * (it is created from HQ), so a company that isn't one is redirected back to
 * the integrations overview (e.g. a stale deep link).
 */
export const RecordingS3IntakeIntegrationDetailSection: FC = () => {
  const integration = useAppSelector(selectRecordingS3IntakeIntegration);

  // The Failed imports tab exists only for a row naming a bucket: its router 404s
  // otherwise (the drain never claims a bucketless row), so a legacy
  // credentials-only RingCX row gets neither the tab nor its badge read. The
  // read is also skipped for a non-customer, so a stale deep link doesn't fire
  // it before the redirect below.
  const hasBucket = checkRecordingS3IntakeBucket(integration);
  const importCounts = useRecordingImportStatusCountsQuery(
    integration && hasBucket ? { integrationId: integration.id } : skipToken,
  );

  if (!integration) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const importIssueCount = importCounts.data
    ? countRecordingImportCustomerRows(importCounts.data)
    : 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    ...(hasBucket
      ? [
          {
            label:
              importIssueCount > 0
                ? `Failed imports (${importIssueCount.toLocaleString()})`
                : "Failed imports",
            to: "failed-imports",
          },
        ]
      : []),
  ];

  const context: RecordingS3IntakeDetailOutletContext = { integration };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo provider={integration.provider} />
              <span className="truncate">{TITLE}</span>
            </span>
          }
          tabs={
            <RouteTabs ariaLabel="Recording S3 intake sections" tabs={tabs} />
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
